import { useState } from 'react';
import { useParams, Link } from 'wouter';
import { AlertCircle, Plus, AlertTriangle, CheckCircle, Clock, ChevronLeft } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useGetActivity, customInstance } from '@edison/api-client-react';
import { useAuthStore } from '@/store/auth-store.js';
import { CompetenceRecordForm, ConfirmRecordButton } from './competence-record-form.js';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';

const IP_BADGE_LABEL: Record<string, string> = {
  company: 'Company IP',
  client: 'CLIENT IP — HIGH RISK',
  joint: 'Joint IP',
  licensed_back: 'Licensed back',
  undetermined: 'Undetermined — Blocks Locking',
};

type BadgeVariant = 'default' | 'positive' | 'warning' | 'info' | 'destructive';

function ipOwnershipVariant(ownership: string): BadgeVariant {
  switch (ownership) {
    case 'company': return 'positive';
    case 'client': return 'destructive';
    case 'undetermined': return 'warning';
    default: return 'default';
  }
}

interface ActivityTimeEntry {
  id: string;
  startTime: string;
  endTime: string;
  rdPercentage: string | null;
  reviewStatus: string;
  userId: string;
}

function useActivityTimeEntries(activityId: string) {
  return useQuery<{ data: { items: ActivityTimeEntry[] } }>({
    queryKey: ['activities', activityId, 'time-entries'],
    queryFn: () => customInstance<{ data: { items: ActivityTimeEntry[] } }>({
      url: `/api/activities/${activityId}/time-entries`,
      method: 'GET',
    }),
    enabled: !!activityId,
  });
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function durationHours(start: string, end: string) {
  return ((new Date(end).getTime() - new Date(start).getTime()) / 3_600_000).toFixed(1);
}

export function ActivityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [showForm, setShowForm] = useState(false);
  const { user: currentUser } = useAuthStore();

  const { data, isLoading, error, refetch } = useGetActivity(id ?? '');
  const { data: entriesData, isLoading: entriesLoading } = useActivityTimeEntries(id ?? '');

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-64 rounded-lg skeleton" />
        <div className="h-32 w-full rounded-lg skeleton" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-center gap-2 text-destructive text-sm">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>Failed to load activity.</span>
      </div>
    );
  }

  const activity = data?.data;
  if (!activity) {
    return <p className="text-muted-foreground">Activity not found.</p>;
  }

  const ipLabel = IP_BADGE_LABEL[activity.ipOwnership];
  const competenceRecords = activity.competenceRecords ?? [];

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <Link
          href={activity.projectId ? `/projects/${activity.projectId}?tab=activities` : '/projects'}
          className="mb-2 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          {activity.projectId ? 'Back to project' : 'Projects'}
        </Link>
        <div className="flex items-start gap-3">
          <h1 className="text-xl font-semibold">{activity.name}</h1>
          {ipLabel && (
            <Badge variant={ipOwnershipVariant(activity.ipOwnership)}>
              {ipLabel}
            </Badge>
          )}
        </div>
        {activity.description && (
          <p className="text-sm text-muted-foreground mt-1">{activity.description}</p>
        )}
      </div>

      {activity.ipOwnership === 'client' && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-1" aria-hidden="true" />
          <span>
            <strong>HIGH RISK — Client IP:</strong> Activities where the client owns the IP may not
            qualify for the R&D Tax Incentive. This is flagged in the claim validation checklist.
          </span>
        </div>
      )}

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-medium">Competence records</h2>
          {!showForm && (
            <Button variant="primary" size="sm" onClick={() => setShowForm(true)}>
              <Plus className="h-3 w-3" />
              Add my record
            </Button>
          )}
        </div>

        {showForm && (
          <CompetenceRecordForm
            activityId={activity.id}
            onSuccess={() => {
              setShowForm(false);
              void refetch();
            }}
            onCancel={() => setShowForm(false)}
          />
        )}

        {!showForm && competenceRecords.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Use the button above to add your competence record for this activity.
          </p>
        )}

        {competenceRecords.map((record) => {
          const isMyRecord = record.assessorUserId === currentUser?.id;
          const needsConfirm = !record.confirmedAt && isMyRecord;
          return (
          <div key={record.id} className="rounded-lg border p-4 space-y-3 mb-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Assessor record</span>
              {record.confirmedAt ? (
                <Badge variant="positive">
                  <CheckCircle className="h-3 w-3 mr-1" aria-hidden="true" />
                  Confirmed
                </Badge>
              ) : (
                <Badge variant="warning">
                  <Clock className="h-3 w-3 mr-1" aria-hidden="true" />
                  Pending confirmation
                </Badge>
              )}
            </div>
            {needsConfirm && (
              <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-800 space-y-2">
                <p>
                  Your record is saved but not yet confirmed. Confirmation is your formal attestation that
                  the information is accurate — it's required before this activity can be included in a
                  claim submission.
                </p>
                <ConfirmRecordButton
                  activityId={activity.id}
                  recordId={record.id}
                  onConfirmed={() => void refetch()}
                />
              </div>
            )}
            {record.qualifications && (
              <div>
                <p className="text-xs text-muted-foreground font-medium mb-0.5">Qualifications</p>
                <p className="text-sm">{record.qualifications}</p>
              </div>
            )}
            {record.relevantExpertise && (
              <div>
                <p className="text-xs text-muted-foreground font-medium mb-0.5">Relevant expertise</p>
                <p className="text-sm">{record.relevantExpertise}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground font-medium mb-0.5">Role on activity</p>
              <p className="text-sm">{record.roleOnActivity}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium mb-0.5">Basis for technical uncertainty</p>
              <p className="text-sm">{record.uncertaintyBasis}</p>
            </div>
          </div>
          );
        })}
      </section>

      <section>
        <h2 className="font-medium mb-3">Time entries</h2>
        {entriesLoading && (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <div key={i} className="h-12 w-full rounded-lg skeleton" />)}
          </div>
        )}
        {!entriesLoading && (entriesData?.data?.items ?? []).length === 0 && (
          <p className="text-sm text-muted-foreground">
            No time entries matched to this activity yet. Entries are matched automatically during classification.
          </p>
        )}
        {!entriesLoading && (entriesData?.data?.items ?? []).length > 0 && (
          <div className="rounded-lg border divide-y">
            {(entriesData?.data?.items ?? []).map((entry) => (
              <div key={entry.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                <span className="text-muted-foreground">{fmtDate(entry.startTime)}</span>
                <span>{durationHours(entry.startTime, entry.endTime)} h</span>
                <span className="text-muted-foreground">
                  {entry.rdPercentage != null ? `${parseFloat(entry.rdPercentage).toFixed(0)}% R&D` : '—'}
                </span>
                <Badge
                  variant={
                    entry.reviewStatus === 'approved' ? 'positive' :
                    entry.reviewStatus === 'rejected' ? 'destructive' :
                    'default'
                  }
                >
                  {entry.reviewStatus}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
